import { createReducer, on } from '@ngrx/store';
import {
  downloadDrawing,
  downloadDrawingFailure,
  downloadDrawingSuccess, loadCommonDrawingBlob, loadCommonDrawingBlobFailure, loadCommonDrawingBlobSuccess,
  loadCommonDrawings,
  loadCommonDrawingsFailure,
  loadCommonDrawingsSuccess,
  modifyDrawing, modifyDrawingFailure,
  modifyDrawingSuccess, removeBulkDrawing, removeBulkDrawingFailure, removeBulkDrawingSuccess,
  saveCommonDrawingScribbleSuccess,
  uploadCommonDrawings,
} from "./drawing.action";
import {
  toggleDrawingRedlineSuccess,
  toggleDrawingNonConformanceSuccess,
} from "../../../cad-model-upload/state/cad-model-revision-drawing/cad-model-revision-drawing.action";
import { StateStatus } from "../../../shared/models/state-status.models";
import { Drawing } from "../../models/drawing.model";

export interface DrawingState {
  drawings: Drawing[];
  error: string | null;
  status: StateStatus;
  downloadDrawingStatus: StateStatus;
  commonDrawingBlobMap: any;
}

export const initialState: DrawingState = {
  drawings: [],
  error: null,
  commonDrawingBlobMap: {} as any,
  status: 'pending' as StateStatus,
  downloadDrawingStatus: 'pending' as StateStatus,
};

export const drawingReducer = createReducer(
  initialState,
  on(loadCommonDrawings, (state) => ({ ...state, status: 'loading' as StateStatus })),
  on(loadCommonDrawingsSuccess, (state, { drawings }) => ({
    ...state,
    drawings: drawings,
    error: null,
    status: 'loadSuccess' as StateStatus,
  })),
  on(loadCommonDrawingsFailure, (state, { error }) => ({
    ...state,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(downloadDrawing, (state) => ({
    ...state,
    downloadDrawingStatus: 'loading' as StateStatus,
  })),
  on(downloadDrawingSuccess, (state, { file }) => ({
    ...state,
    downloadDrawingStatus: 'loadSuccess' as StateStatus,
  })),
  on(downloadDrawingFailure, (state, { error }) => ({
    ...state,
    error: error,
    downloadDrawingStatus: 'loadError' as StateStatus,
  })),
  on(removeBulkDrawing, (state, { drawingIds }) => ({
    ...state,
    status: 'removing' as StateStatus,
    drawings: state.drawings.filter(drawing => !drawingIds.includes(drawing.id)),
  })),
  on(removeBulkDrawingSuccess, (state) => ({
    ...state,
    status: 'removeSuccess' as StateStatus,
  })),
  on(removeBulkDrawingFailure, (state, { error }) => ({
    ...state,
    error: error,
    status: 'removeError' as StateStatus,
  })),
  on(modifyDrawing, (state) => ({
    ...state,
    status: 'modifying' as StateStatus,
  })),
  on(modifyDrawingSuccess, (state) => ({
    ...state,
    status: 'modifySuccess' as StateStatus,
  })),
  on(modifyDrawingFailure, (state, { error }) => ({
    ...state,
    error: error,
    status: 'modifyError' as StateStatus,
  })),
  on(uploadCommonDrawings, (state) => ({ ...state, status: 'loading' as StateStatus })),
  on(loadCommonDrawingBlob, (state, { pdfDrawingFileKey }): DrawingState => ({
    ...state,
    commonDrawingBlobMap: {
      ...state.commonDrawingBlobMap,
      [pdfDrawingFileKey]: partNumberDrawingBlobValues(pdfDrawingFileKey, state.commonDrawingBlobMap),
    }
  })),
  on(loadCommonDrawingBlobSuccess, (state, { pdfDrawingFileKey, blob }): DrawingState => ({
    ...state,
    commonDrawingBlobMap: {
      ...state.commonDrawingBlobMap,
      [pdfDrawingFileKey]: {
        blob: blob,
        completed: true,
        inProgress: false,
        previousCallInProgress: false
      },
    }
  })),
  on(loadCommonDrawingBlobFailure, (state, { error }): DrawingState => ({
    ...state,
    error: error,
  })),
  on(saveCommonDrawingScribbleSuccess, (state, { pdfDrawingFileKey, blob }): DrawingState => ({
    ...state,
    commonDrawingBlobMap: {
      ...state.commonDrawingBlobMap,
      [pdfDrawingFileKey]: {
        blob,
        completed: true,
        inProgress: false,
        previousCallInProgress: false
      },
    }
  })),
  on(toggleDrawingRedlineSuccess, (state, { drawing }) => ({
    ...state,
    drawings: state.drawings.map(d => d.id === drawing.id ? { ...d, isRedline: drawing.isRedline } : d),
  })),
  on(toggleDrawingNonConformanceSuccess, (state, { drawing }) => ({
    ...state,
    drawings: state.drawings.map(d => d.id === drawing.id ? { ...d, isNonConformance: drawing.isNonConformance } : d),
  })),
);

function partNumberDrawingBlobValues(pdfDrawingFileKey: string, commonDrawingBlobMap: any) {
  if (pdfDrawingFileKey in commonDrawingBlobMap) {
    return {
      ...commonDrawingBlobMap[pdfDrawingFileKey],
      previousCallInProgress: commonDrawingBlobMap[pdfDrawingFileKey].inProgress
    };
  } else {
    return {
      blob: undefined,
      inProgress: true,
      previousCallInProgress: false,
      completed: false,
    }
  }
}
